import { rpc } from '@server/infrastructure/rpc'
import {
  AiCharacterChatDifficultyType,
  AiLectureCourseResponse,
  LectureCourseLangType,
  LectureCourseResponse,
  LangType,
} from '@server/schemas'
import { queryOptions } from '@tanstack/react-query'

import {
  getSubscribeMappList,
  getSubscribeMappUsed,
  getSubscribeMappUsing,
  getTicketSubscribeList,
  getTrialSubscribesByStudentId,
} from '../api'

type LectureCourseRequest = RequestQueryOptions<{
  langType: LectureCourseLangType
  locale: string
  includeAllTrialLessons?: boolean
}>

const fetchLectureCourseResponse = async (requestDto: LectureCourseRequest): Promise<LectureCourseResponse> => {
  const response = await rpc.api.v1.subscribes['lecture-course-list'].$get(
    {
      query: {
        langType: requestDto.langType,
        ...(requestDto.includeAllTrialLessons ? { includeAllTrialLessons: 'true' as const } : {}),
      },
    },
    {
      headers: {
        Authorization: `Bearer ${requestDto.bearerToken}`,
        'Accept-Language': requestDto.locale,
      },
    },
  )

  if (!response.ok) {
    throw new Error(`Failed to fetch lecture courses: ${response.status}`)
  }

  return (await response.json()) as LectureCourseResponse
}

export const subscribesEntityQueries = {
  all: ['subscribes'],

  // queryKeys
  getSubscribeMappListQueryKey: ({ bearerToken }: RequestHeaderOptions) => [
    ...subscribesEntityQueries.all,
    'GET_SUBSCRIBE_MAPP_LIST',
    bearerToken,
  ],
  getTicketSubscribeListQueryKey: ({ bearerToken }: RequestHeaderOptions) => [
    ...subscribesEntityQueries.all,
    'GET_TICKET_SUBSCRIBE_LIST',
    bearerToken,
  ],
  getTrialSubscribesByStudentIdQueryKey: (requestDto: RequestQueryOptionalOptions<{ studentId: number }>) => [
    ...subscribesEntityQueries.all,
    'GET_TRIAL_SUBSCRIBES_BY_STUDENT_ID',
    'KR_PRODUCT_V1',
    requestDto,
  ],
  getLectureCourseListQueryKey: (requestDto: LectureCourseRequest) => [
    ...subscribesEntityQueries.all,
    'GET_LECTURE_COURSE_LIST',
    requestDto,
  ],
  getLectureCourseResponseQueryKey: (requestDto: LectureCourseRequest) => [
    ...subscribesEntityQueries.all,
    'GET_LECTURE_COURSE_RESPONSE',
    requestDto,
  ],
  getAiLectureCourseListQueryKey: (
    requestDto: RequestQueryOptions<{
      langType: Exclude<LangType, 'ENJP'>
      difficulty: AiCharacterChatDifficultyType
      locale: string
    }>,
  ) => [...subscribesEntityQueries.all, 'GET_AI_LECTURE_COURSE_LIST', requestDto],

  getSubscribeMappUsedQueryKey: ({ bearerToken }: RequestHeaderOptions) => [
    ...subscribesEntityQueries.all,
    'GET_SUBSCRIBE_MAPP_USED',
    bearerToken,
  ],

  getActiveSubscriptionsQueryKey: ({ bearerToken }: RequestHeaderOptions) => [
    ...subscribesEntityQueries.all,
    'GET_ACTIVE_SUBSCRIPTIONS',
    bearerToken,
  ],

  // queryOptions
  getSubscribeMappUsed: ({ bearerToken }: RequestHeaderOptions) =>
    queryOptions({
      queryKey: subscribesEntityQueries.getSubscribeMappUsedQueryKey({ bearerToken }),
      queryFn: async () => getSubscribeMappUsed(bearerToken),
    }),
  getActiveSubscriptions: ({ bearerToken }: RequestHeaderOptions) =>
    queryOptions({
      queryKey: subscribesEntityQueries.getActiveSubscriptionsQueryKey({ bearerToken }),
      queryFn: async (): Promise<{ name: string }[]> => getSubscribeMappUsing(bearerToken),
    }),
  getSubscribeMappList: (requestDto: RequestHeaderOptions & { timeoutMs?: number }) =>
    queryOptions({
      queryKey: subscribesEntityQueries.getSubscribeMappListQueryKey(requestDto),
      queryFn: async ({ signal }) =>
        getSubscribeMappList(requestDto.bearerToken, { signal, timeoutMs: requestDto.timeoutMs }),
    }),
  getTicketSubscribeList: (requestDto: RequestHeaderOptions) =>
    queryOptions({
      queryKey: subscribesEntityQueries.getTicketSubscribeListQueryKey(requestDto),
      queryFn: async () => getTicketSubscribeList(requestDto.bearerToken),
    }),
  getLectureCourseList: (requestDto: LectureCourseRequest) =>
    queryOptions({
      queryKey: subscribesEntityQueries.getLectureCourseListQueryKey(requestDto),
      queryFn: async (): Promise<LectureCourseResponse['subscribes']> => {
        const result = await fetchLectureCourseResponse(requestDto)

        return result.subscribes
      },
    }),
  getLectureCourseResponse: (requestDto: LectureCourseRequest) =>
    queryOptions({
      queryKey: subscribesEntityQueries.getLectureCourseResponseQueryKey(requestDto),
      queryFn: () => fetchLectureCourseResponse(requestDto),
    }),
  getAiLectureCourseList: (
    requestDto: RequestQueryOptions<{
      langType: Exclude<LangType, 'ENJP'>
      difficulty: AiCharacterChatDifficultyType
      locale: string
    }>,
  ) =>
    queryOptions({
      queryKey: subscribesEntityQueries.getAiLectureCourseListQueryKey(requestDto),
      queryFn: async (): Promise<AiLectureCourseResponse> => {
        const response = await rpc.api.v1.subscribes['lecture-course-list'].ai.$get(
          {
            query: { langType: requestDto.langType, difficulty: requestDto.difficulty },
          },
          {
            headers: { Authorization: `Bearer ${requestDto.bearerToken}`, 'Accept-Language': requestDto.locale },
          },
        )

        if (!response.ok) {
          throw new Error(`Failed to fetch lecture courses: ${response.status}`)
        }

        return response.json() as Promise<AiLectureCourseResponse>
      },
    }),
  getTrialSubscribesByStudentId: ({
    timeoutMs,
    ...requestDto
  }: RequestQueryOptionalOptions<{ studentId: number }> & { timeoutMs?: number }) =>
    queryOptions({
      queryKey: subscribesEntityQueries.getTrialSubscribesByStudentIdQueryKey(requestDto),
      queryFn: async () => await getTrialSubscribesByStudentId({ ...requestDto, timeoutMs }),
    }),
} as const
